import React, { createContext, useContext, useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import i18n from "../i18n";

import { setPromoColors } from "./utils";
import { createFontFaceStyle } from "./fontFaces";
import { LOBBY_CONFIG_DEFAULTS } from "./lobbyConfigDefaults";
import { isSweepOrg } from "../utils/sweepUtils";
import { isNoGradientOrg, isSoccerbetOrg } from "../utils/brandUtils";
import httpClient from "@/api/httpClient.ts";

import PageNotFound from "../components/pageNotFound/PageNotFound";
import PageSkeleton from "@/components/skeleton/PageSkeleton";

const LobbyConfigContext = createContext<any>(null);

const setColorVariables = (paramKey: string, paramValue: string) => {
  document.documentElement.style.setProperty(`${paramKey}`, `${paramValue}`);
};

const setLobbyBackground = (bgString: string | undefined) => {
  if (!bgString || bgString.length === 0) {
    return;
  }
  let bgObj;
  try {
    bgObj = JSON.parse(bgString);
  } catch {
    console.warn("Invalid lobby background JSON");
    return;
  }
  let desktopUri = bgObj?.["desktop"]?.[0]?.uri;
  let mobileUri = bgObj?.["mobile"]?.[0]?.uri;

  if (!desktopUri && !mobileUri) {
    const enUri = bgObj?.["en"]?.[0]?.uri;
    if (enUri) {
      desktopUri = enUri;
      mobileUri = enUri;
    }
  }
  if (desktopUri) {
    setColorVariables("--background-image-web-lobby", `url(${desktopUri})`);
    setColorVariables("--background-image-web", `url(${desktopUri})`);
  }
  if (mobileUri) {
    setColorVariables("--background-image-mobile-lobby", `url(${mobileUri})`);
    setColorVariables("--background-image-mobile", `url(${mobileUri})`);
  }
};

const loadFonts = async (fontsJson: string | undefined, lang: string) => {
  if (!fontsJson) return;

  try {
    const fonts = JSON.parse(fontsJson);
    if (!Array.isArray(fonts) || fonts.length === 0) return;

    const selectedLang =
      fonts.find((font: any) => font.lang === lang) ||
      fonts.find((font: any) => font.default) ||
      fonts[0];

    if (selectedLang && selectedLang.fontFamily) {
      Object.entries(selectedLang.fontFamily).forEach(([fontWeight, fontPath]) => {
        if (typeof fontPath === "string") {
          createFontFaceStyle(fontPath, fontWeight);
        }
      });
    }
  } catch (error) {
    console.error("Failed to load fonts:", error);
  }
};

const parseLayout = (layout: string) => {
  try {
    return JSON.parse(layout);
  } catch (error) {
    return null;
  }
};

export const LobbyConfigProvider = ({
  dataRoute,
  children,
  loadingFallback,
  errorFallback,
}: {
  children: React.ReactNode;
  dataRoute: string;
  loadingFallback?: React.ReactNode;
  errorFallback?: React.ReactNode;
}) => {
  const {
    data: lobbyConfig,
    isLoading,
    isError,
  } = useQuery({
    queryKey: ["lobbyConfig", dataRoute],
    queryFn: () =>
      httpClient.get(dataRoute).then((response) => {
        const fallback = LOBBY_CONFIG_DEFAULTS[dataRoute];
        return fallback && typeof response.data === "string" ? { data: fallback } : response;
      }),
    select: (lobbyConfig) => lobbyConfig.data,
  });

  const layout = useMemo(
    () => (lobbyConfig ? parseLayout(lobbyConfig.layout) : null),
    [lobbyConfig]
  );
  const isReady = !isError && !isLoading && !!lobbyConfig && !!layout;
  const language = i18n.language;

  // Applied while rendering, before the children render: some of them read the theme colors
  // from <html> in their own render (NavIcon, FilterIcon).
  useMemo(() => {
    if (!isReady) return;
    loadFonts(lobbyConfig.fonts, language);
    setPromoColors(layout);
    setLobbyBackground(lobbyConfig.background);
    document.documentElement.classList.toggle(
      "no-gradients",
      isNoGradientOrg(lobbyConfig?.orgUuid)
    );
  }, [isReady, lobbyConfig, layout, language]);

  const contextValue = useMemo(
    () =>
      isReady
        ? {
            ...lobbyConfig,
            isSweep: isSweepOrg(lobbyConfig?.orgUuid),
            isSoccerbet: isSoccerbetOrg(lobbyConfig?.orgUuid),
          }
        : null,
    [isReady, lobbyConfig]
  );

  if (isError) {
    return <>{errorFallback ?? <PageNotFound />}</>;
  }
  if (isLoading) {
    return <>{loadingFallback ?? <PageSkeleton />}</>;
  }

  if (!lobbyConfig) {
    return <>{errorFallback ?? <PageNotFound />}</>;
  }

  if (!layout) {
    return <>{errorFallback ?? <PageNotFound />}</>;
  }

  return <LobbyConfigContext.Provider value={contextValue}>{children}</LobbyConfigContext.Provider>;
};

export const useLobbyConfig = () => {
  return useContext(LobbyConfigContext);
};

export { LobbyConfigContext };
